/* eslint-disable rxjs/no-exposed-subjects */
import { Type, NgZone } from '@angular/core';

import { BehaviorSubject, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import * as am4core from '@amcharts/amcharts4/core';
import { SerialChart, Series, ValueAxis } from '@amcharts/amcharts4/charts';
import { Container } from '@amcharts/amcharts4/core';
import moment from 'moment-timezone';

import { SeriesModel } from './series.model';
import { DataPointModel } from './data-point.model';
import { chartColors } from '../enums/chart-colors.enum';
import { defaultSeriesColorConfig } from '../config/default-series-color.config';
import { AbstractChartOptionsModel } from './abstract-chart-options.model';
import { ChartMetadataModel } from './chart-metadata.model';
import { CustomLegendItem } from './custom-legend-item.model';
import { image } from '../../Misc/helpers/image/image.helper';
import { ChartStylesModel } from './chart-styles.model';
import { abbreviateNumber } from '../../Misc/helpers/formatting/number.helper';
import { userAppPreferencesDefault } from '../../../Core/Users/data/user-app-preferences-default';
import { AxisLineModel } from './lineChart/axis-line.model';
import { ChartSeries } from './chart-series.model';

// these only need to be done once globally
am4core.options.commercialLicense = true;
// @TODO: Turning this on will make it more efficient, but sometimes the title gets smushed in firefox (NEP-3816),
//  we should investigate how to fix that so we can turn this back on
am4core.options.queue = false;
// @TODO: Turning this on will make it more efficient, but when the chart gets scrolled into view it's not re-rendered, and the export is also not re-rendered
// Turned off for AD-10370, turning back on will require more investigation
am4core.options.onlyShowOnViewport = false;

// need this to target certain amchart elements with css queries
am4core.options.autoSetClassName = false;

export abstract class ChartModel {
    public amchart: SerialChart;
    public amseries: Series;
    public readonly chartType: Type<SerialChart>;

    public header: am4core.Container;
    protected titleLabel: am4core.Label;
    protected subtitleLabel: am4core.Label;
    protected metadataContainer: am4core.Container;
    public primarySeriesLabel: am4core.TextLink;
    public secondarySeriesLabel: am4core.TextLink;
    public dateRangeLabel: am4core.Label;
    public noContentLabel: am4core.Label;

    protected element: HTMLElement;

    protected chartWrapper: HTMLDivElement;
    protected legendWrapper: HTMLDivElement;

    public rawSeries: SeriesModel[];
    public series: SeriesModel[];
    public singleSeries: boolean;

    public metadata: ChartMetadataModel;
    public options: AbstractChartOptionsModel;
    public chartSeries: ChartSeries[] = [];

    public dataSubscriptions: Subscription[] = [];
    public rawData: DataPointModel[][];
    public previousPeriodRawData: DataPointModel[][];
    protected zone: NgZone;
    protected isForSharedReport: boolean = false;
    isPreview: boolean;

    overrideNoContent: boolean = false;

    readonly defaultChartColors = userAppPreferencesDefault.reportsPrefs.defaultChartColors;

    public ready: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public styles: ChartStylesModel;

    constructor(
        series: SeriesModel[],
        options: AbstractChartOptionsModel = {},
        metadata?: ChartMetadataModel,
    ) {
        this.rawSeries = series;
        this.options = options || {};
        this.styles = this.options.styles || new ChartStylesModel();
        this.metadata = metadata;
        this.series = this.breakdownSeries(series);
    }

    private breakdownSeries(series: SeriesModel[]): SeriesModel[] {
        const breakdown: SeriesModel[] = [];

        series.forEach(s => {
            if (s.subSeries?.length > 0) {
                s.subSeries.forEach(subS => {
                    const subSeries: SeriesModel = {
                        ...subS,
                        dataProvider: s.dataProvider.pipe(map(dataPoints => {
                            if (!Array.isArray(dataPoints)) {
                                return dataPoints;
                            }
                            return dataPoints.map(dp => {
                                return {
                                    ...dp,
                                    yValue: +dp[subS.key] || 0
                                };
                            });
                        }))
                    };

                    // check if we set a color for this subSeries in the colorMap
                    if (this.styles.colorMap[subSeries.key]) {
                        subSeries.color = this.styles.colorMap[subSeries.key];
                    }
                    breakdown.push(subSeries);
                });
            } else {
                breakdown.push(s);
            }
        });

        return breakdown;
    }

    /**
     * Destroys the chart
     */
    public destroy() {
        this.chartWrapper?.remove();
        this.legendWrapper?.remove();
        if (this.amchart) {
            this.amchart.data = null;
        }

        requestAnimationFrame(() => {
            if (this.amchart) {
                this.amchart.dispose();
                this.amchart = null;
            }
        });
    }

    // Can't have a definition here because you can't instantiate ChartModel, can't mark it abstract or it can't be optional
    public clone?(): ChartModel;

    public setZone(zone: NgZone) {
        this.zone = zone;
    }

    private makeSeriesLabel(parent: am4core.Container, labelText: string, url?: string, id?: string, displaySourceAsLink?: boolean): am4core.TextLink {
        // TextLink object for the series name / link to source
        let seriesLabel = parent.createChild(am4core.Label);
        if (displaySourceAsLink) {
            seriesLabel = parent.createChild(am4core.TextLink);
            seriesLabel.events.on('hit', (event) => {
                if (!event?.target?.url && this.rawSeries &&
                    this.rawSeries?.[0]?.['mentions']?.['socialTopicId']
                ) {
                    // eslint-disable-next-line max-len
                    alert('The Social Topic you were attempting to load is not viewable since it is not associated with any Themes. Please associate the Social Topic with a Theme and try again.');
                }
            });
            seriesLabel.url = url;
            seriesLabel.urlTarget = '_blank';
            seriesLabel.textDecoration = 'underline';
            seriesLabel.states.getKey('hover').properties.fill = am4core.color(chartColors.agprInteractive);
            seriesLabel.states.getKey('down').properties.fill = am4core.color(chartColors.agprInteractive);
        }
        seriesLabel.id = id;
        seriesLabel.fontFamily = 'Poppins, Arial, sans-serif';
        seriesLabel.text = labelText;
        seriesLabel.focusable = true;
        seriesLabel.fill = am4core.color(chartColors.agprInteractive);
        // Removed the if statement to change the hover properties

        seriesLabel.fontSize = '0.781rem';
        seriesLabel.align = 'right';
        seriesLabel.textAlign = 'end';
        seriesLabel.width = am4core.percent(50);
        seriesLabel.truncate = true;

        seriesLabel.tooltip = new am4core.Tooltip();
        seriesLabel.tooltip.getFillFromObject = false;
        seriesLabel.tooltip.label.fill = am4core.color(chartColors.agprText);
        seriesLabel.tooltip.background.fill = am4core.color(chartColors.agprWhite);
        seriesLabel.tooltip.background.strokeOpacity = 1;
        seriesLabel.tooltip.background.fillOpacity = 1;
        seriesLabel.tooltip.background.cornerRadius = 3;
        seriesLabel.tooltipText = labelText;
        seriesLabel.tooltip.dy = 10;
        seriesLabel.tooltip.dx = 50;
        seriesLabel.tooltip.pointerOrientation = 'up';

        return seriesLabel;
    }

    /**
     * Styles a tooltip as the white card the rest of the charts use. Only for tooltips whose
     * content is HTML - the plain text ones are filled with their series' colour instead.
     */
    protected styleHtmlTooltip(tooltip: am4core.Tooltip): void {
        tooltip.getFillFromObject = false;
        tooltip.background.fill = am4core.color(chartColors.agprWhite);
        tooltip.background.stroke = am4core.color('#ced4da');
        tooltip.background.cornerRadius = 8;
        tooltip.background.fillOpacity = 1;
        // clear tooltip background shadow
        tooltip.background.filters.clear();
        tooltip.label.padding(0, 0, 0, 0);
        tooltip.label.fill = am4core.color(chartColors.agprBlack);
        tooltip.label.interactionsEnabled = true;
        tooltip.label.wrap = true;
        tooltip.keepTargetHover = true;
        tooltip.maxWidth = 275;
        // Keeps the tooltip out of the chart's own bounds calculation, so a wide one near an edge
        // is moved rather than clipped
        tooltip.ignoreBounds = true;

        if (this.amchart) {
            // Left to itself the tooltip is placed against the point it belongs to, which cuts it
            // off on a narrow chart. Bounding it to the chart makes it flip to the other side.
            tooltip.boundingContainer = this.amchart;
        }
    }

    /**
     * Creates a line that will appear along an axis
     */
    protected addAxisLine(axis: ValueAxis, lineDef: AxisLineModel, isVertical: boolean) {
        const line = axis.axisRanges.create();
        line.value = lineDef.position;
        line.endValue = lineDef.position;
        line.grid.stroke = am4core.color(lineDef.color);
        line.grid.strokeWidth = lineDef.strokeWidth || 1;
        line.grid.strokeOpacity = lineDef.strokeOpacity || 1;
        if (lineDef.strokeDashArray) {
            line.grid.strokeDasharray = lineDef.strokeDashArray;
        }
        if (lineDef.name) {
            if (!this.options['showLinesInLegend']) {
                line.label.text = lineDef.name;
            }
            line.label.fill = line.grid.stroke;
            line.label.inside = true;
            if (isVertical) {
                line.label.valign = 'top';
                line.label.horizontalCenter = 'left';
            } else {
                line.label.verticalCenter = 'bottom';
            }
        }
        if (lineDef.tooltip || lineDef.tooltipHTML) {
            // Need to create a separate range to make the tooltip able to appear for more than the default line pixel size
            const tooltipLine = axis.axisRanges.create();
            let range = 1;
            if (isVertical) {
                if (this.options['maxXValue']) {
                    range = (this.options['maxXValue'] - (this.options['minXValue'] || 0)) / 50;
                }
            } else {
                if (this.options['maxYValue']) {
                    range = (this.options['maxYValue'] - (this.options['minYValue'] || 0)) / 50;
                }
            }
            tooltipLine.value = lineDef.position - range / 2;
            tooltipLine.endValue = lineDef.position + range / 2;
            tooltipLine.grid.strokeOpacity = 0;
            tooltipLine.axisFill.tooltip = new am4core.Tooltip();
            if (lineDef.tooltipHTML) {
                tooltipLine.axisFill.tooltipHTML = lineDef.tooltipHTML;
                this.styleHtmlTooltip(tooltipLine.axisFill.tooltip);
            } else {
                tooltipLine.axisFill.tooltipText = lineDef.tooltip;
            }
            if (lineDef.tooltip && this.options['addTooltipValue']) {
                if (isVertical) {
                    // for some reason the minutes & hours are off, will look more into it later
                    tooltipLine.axisFill.tooltipText += ` - ${moment(lineDef.position).format('YYYY-MM-DD HH:mm')}`;
                } else {
                    tooltipLine.axisFill.tooltipText += ' - {value}';
                }
            }
            tooltipLine.axisFill.interactionsEnabled = true;
            tooltipLine.axisFill.isMeasured = true;
        }
    }

    /**
     * Initializes the chart into the DOM
     */
    public initChart(element: HTMLElement | Container, chartType: Type<SerialChart>): void {
        if (element instanceof HTMLElement) {
            this.element = element;
            this.chartWrapper = document.createElement('div');
            this.chartWrapper.style.width = '100%';
            this.chartWrapper.style.height = '100%';
            element.appendChild(this.chartWrapper);

            // create the chart itself
            this.amchart = am4core.create(this.chartWrapper, chartType);
            this.amchart.focusable = false;
            this.amchart.focusFilter = undefined;

        } else {
            this.amchart = element.createChild(chartType);
        }

        this.setDefaultPadding();
        this.isForSharedReport = !!this.options.responsive;

        this.amchart.events.on('sizechanged', ev => {
            const chart = ev.target;
            if (chart.pixelWidth < 300) {
                if (this.titleLabel) {
                    this.titleLabel.tooltipText = this.metadata.titleText;
                    if (this.titleLabel.tooltip) {
                        this.titleLabel.tooltip.getFillFromObject = false;
                        this.titleLabel.tooltip.label.fill = am4core.color(chartColors.agprText);
                        this.titleLabel.tooltip.background.fill = am4core.color(chartColors.agprWhite);
                        this.titleLabel.tooltip.background.strokeOpacity = 1;
                        this.titleLabel.tooltip.background.fillOpacity = 1;
                        this.titleLabel.tooltip.background.cornerRadius = 3;
                        this.titleLabel.tooltip.dy = 10;
                    }
                }
                if (this.primarySeriesLabel) {
                    this.primarySeriesLabel.tooltip.dx = 0;
                }
                if (this.secondarySeriesLabel) {
                    this.secondarySeriesLabel.tooltip.dx = 0;
                }
            } else {
                if (this.titleLabel) {
                    this.titleLabel.tooltipText = '';
                }
                if (this.primarySeriesLabel) {
                    this.primarySeriesLabel.tooltip.dx = 50;
                }
                if (this.secondarySeriesLabel) {
                    this.secondarySeriesLabel.tooltip.dx = 50;
                }
            }
        });

        // default header if not specified
        if ((this.options.header ?? true) && this.metadata && !this.options.styles?.chartDisplaySettings?.hasNoElements() && !this.options.responsive) {
            // Header is the container for everything on top of the chart that isn't the chart itself
            this.header = this.amchart.createChild(am4core.Container);
            this.header.width = am4core.percent(100);
            this.header.layout = 'vertical';
            this.header.marginBottom = 10;
            this.header.toBack();

            if (this.options.styles?.chartDisplaySettings?.title) {
                this.titleLabel = this.header.createChild(am4core.Label);
                this.titleLabel.fontFamily = 'Poppins, Arial, sans-serif';
                this.titleLabel.focusable = true;
                if (this.metadata.titleHeadingLevel) {
                    this.titleLabel.html = `
                        <span role="heading" aria-level="${this.metadata.titleHeadingLevel}" style="margin: 0 0 5px;">
                            ${this.metadata.titleText}
                        </span>`;
                } else {
                    this.titleLabel.text = this.metadata.titleText;
                }

                if (this.options.styles?.titleFontSize) {
                    this.titleLabel.fontSize = this.options.styles?.titleFontSize;
                } else {
                    this.titleLabel.fontSize = '1rem';
                }

                if (this.options.styles?.titleFontWeight) {
                    this.titleLabel.fontWeight = this.options.styles?.titleFontWeight;
                } else {
                    this.titleLabel.fontWeight = '700';
                }

                this.titleLabel.fill = this.options.styles?.titleLabelColor ?
                    am4core.color(this.options.styles?.titleLabelColor) : am4core.color(chartColors.agprText);
                this.titleLabel.align = 'left';
                this.titleLabel.textAlign = this.options?.styles?.titleAlignment || 'start';
                this.titleLabel.marginBottom = 5;
                this.titleLabel.paddingBottom = 0;

                this.titleLabel.truncate = true;
                this.titleLabel.width = am4core.percent(100);

                if (this.metadata.titleHtml) {
                    // @HACK When setting html instead of text, titleLabel.align center not working
                    // Thus, end up using this hack to make the title label centered
                    this.titleLabel.html = this.metadata.titleHtml;
                    this.titleLabel.adapter.add('html', (text, target) => {
                        return this.metadata.titleHtml.replace('{{width}}', (target.pixelWidth + 15) + 'px');
                    });
                }

                if (this.metadata.titleTooltip) {
                    this.titleLabel.tooltipText = this.metadata.titleTooltip;
                    this.titleLabel.tooltip.getFillFromObject = false;
                    this.titleLabel.tooltip.label.fill = am4core.color(chartColors.agprText);
                    this.titleLabel.tooltip.background.fill = am4core.color(chartColors.agprWhite);
                    this.titleLabel.tooltip.background.strokeOpacity = 1;
                    this.titleLabel.tooltip.background.fillOpacity = 1;
                    this.titleLabel.tooltip.background.cornerRadius = 3;
                    this.titleLabel.tooltip.dy = -10;
                } else {
                    this.titleLabel.tooltipText = '';
                }

                this.titleLabel.events.on('ready', event => {
                    event.target.dom.setAttribute('id', `${this.metadata.id}_title`);
                });
            }

            if (this.options.styles?.chartDisplaySettings?.dateRange || this.options.styles?.chartDisplaySettings?.hasSourceLink()) {
                // Container for metadata
                this.metadataContainer = this.header.createChild(am4core.Container);
                this.metadataContainer.width = am4core.percent(100);
                this.metadataContainer.layout = 'horizontal';

                if (this.options.styles?.chartDisplaySettings?.dateRange) {
                    // Label object for the date range filter
                    this.dateRangeLabel = this.metadataContainer.createChild(am4core.Label);
                    this.dateRangeLabel.fontFamily = 'Poppins, Arial, sans-serif';

                    this.dateRangeLabel.text = this.metadata.dateRangeLabelText;
                    this.dateRangeLabel.focusable = true;
                    this.dateRangeLabel.fontSize = '0.781rem';
                    this.dateRangeLabel.align = 'left';
                    this.dateRangeLabel.textAlign = 'start';
                    this.dateRangeLabel.width = this.singleSeries ? am4core.percent(50) : am4core.percent(100);
                    this.dateRangeLabel.truncate = true;

                    this.dateRangeLabel.tooltip = new am4core.Tooltip();
                    this.dateRangeLabel.tooltip.getFillFromObject = false;
                    this.dateRangeLabel.tooltip.label.fill = am4core.color(chartColors.agprText);
                    this.dateRangeLabel.tooltip.background.fill = am4core.color(chartColors.agprWhite);
                    this.dateRangeLabel.tooltipText = this.metadata.dateRangeLabelText;
                    this.dateRangeLabel.tooltip.background.strokeOpacity = 1;
                    this.dateRangeLabel.tooltip.background.fillOpacity = 1;
                    this.dateRangeLabel.tooltip.background.cornerRadius = 3;

                    // we need to adjust the tooltip position so that it doesn't overflow the chart
                    const length = this.dateRangeLabel?.tooltipText?.length || 0;
                    if (length === 0) {
                        this.dateRangeLabel.tooltipX = 120;
                    } else if (length < 10) {
                        this.dateRangeLabel.tooltipX = length;
                    } else if (length < 20) {
                        this.dateRangeLabel.tooltipX = length + 60;
                    } else if (length < 30) {
                        this.dateRangeLabel.tooltipX = length + 100;
                    } else {
                        this.dateRangeLabel.tooltipX = length + 120;
                    }

                    this.dateRangeLabel.tooltipY = 15;
                    this.dateRangeLabel.tooltip.pointerOrientation = 'up';

                    if (this.options.styles?.spacingUnderDate) {
                        this.dateRangeLabel.marginBottom = 10;
                    }
                }

                if (this.options.styles?.chartDisplaySettings?.hasSourceLink()) {
                    // there can be up to two series labels/links on the chart, figure those out now
                    if (this.metadata.primarySeriesLabelText) {
                        this.primarySeriesLabel = this.makeSeriesLabel(
                            this.metadataContainer,
                            this.metadata.primarySeriesLabelText,
                            this.metadata.primarySeriesUrl,
                            'source',
                            this.options.styles.chartDisplaySettings.displaySourceAsLink
                        );
                        if (!this.options.styles?.chartDisplaySettings?.dateRange) {
                            this.primarySeriesLabel.textAlign = 'start';
                        }
                    }
                    if (this.metadata.secondarySeriesLabelText) {
                        this.secondarySeriesLabel = this.makeSeriesLabel(
                            this.metadataContainer,
                            this.metadata.secondarySeriesLabelText,
                            this.metadata.secondarySeriesUrl,
                            'source2',
                            this.options.styles.chartDisplaySettings.displaySourceAsLink
                        );
                        if (!this.options.styles?.chartDisplaySettings?.dateRange) {
                            this.secondarySeriesLabel.textAlign = 'start';
                        }
                    }
                }
            }
        }
    }

    setDefaultPadding() {
        if (this.options.styles?.customPadding) {
            this.amchart.padding(
                this.options.styles?.customPadding.top,
                this.options.styles?.customPadding.right,
                this.options.styles?.customPadding.bottom,
                this.options.styles?.customPadding.left
            );
        } else {
            if (!this.options.noPadding) {
                this.amchart.padding(15, 15, 15, 15);
            } else {
                this.amchart.padding(0, 0, 0, 0);
            }
        }

        if (!this.options.responsive && this.options?.styles?.chartDisplaySettings?.hasNoElements()) {
            this.amchart.paddingTop = 30;
        }
    }

    protected initCustomLegend(): void {
        this.legendWrapper = document.createElement('div');
        this.legendWrapper.className = 'agpr-customLegend';

        // is just used for drilldown, because there is "techincally" 3 charts on the drilldown, the custom legend gets added 3 times
        while (this.options.legendContainerElement?.firstChild) {
            this.options.legendContainerElement.removeChild(this.options.legendContainerElement.lastChild);
        }
        this.options.legendContainerElement.appendChild(this.legendWrapper);
        this.amchart.events.on('ready', (event) => {
            this.updateCustomLegend();
        });
    }

    protected updateCustomLegend(): void {
        if (!this.legendWrapper) {
            return;
        }

        this.legendWrapper.innerHTML = '';
        const snippet = document.createDocumentFragment();
        const items = this.getCustomLegendItems();
        if (items?.length) {
            items.forEach((item) => {
                snippet.append(this.getCustomLegendItemElement(item));
            });
            this.legendWrapper.append(snippet);
        }
    }
    protected getCustomLegendItems(): CustomLegendItem[] {
        return [];
    }
    protected getCustomLegendItemElement(dataPoint: CustomLegendItem): HTMLElement {
        const item = document.createElement('div');
        item.classList.add('agpr-customLegend-item');
        let html = `
            <span style='background: ${dataPoint.color};' class='agpr-customLegend-itemMarker'></span>
            <span class='agpr-customLegend-itemLabel'>${dataPoint.label}</span>
        `;
        if (dataPoint.count != null) {
            html += `&nbsp;-&nbsp;<span class='agpr-customLegend-itemCount'>${abbreviateNumber(dataPoint.count, { decimals: 2 })}</span>`;
        }
        if (dataPoint.percent != null) {
            html += `&nbsp;-&nbsp;<span class='agpr-customLegend-itemPercent'>${dataPoint.percent}%</span>`;
        }
        if (dataPoint.mouseover) {
            item.addEventListener('mouseover', () => dataPoint.mouseover(dataPoint));
        }
        if (dataPoint.mouseout) {
            item.addEventListener('mouseout', () => dataPoint.mouseout(dataPoint));
        }
        if (dataPoint.click) {
            item.classList.add('agpr-customLegend-item--clickable');
            item.addEventListener('click', () => dataPoint.click(dataPoint));
        }
        item.innerHTML = html;

        return item;
    }

    /**
     * Exports a chart via amcharts
     */
    public abstract export(type: keyof am4core.IExportOptions, filename?: string): Promise<boolean>;

    /**
     * Checks to see if chart content is empty
     */
    public abstract isContentEmpty(): boolean;

    /**
     * Updates the current presentation of data in the chart
     */
    public updateData(data: DataPointModel[][], previousPeriod?: boolean): void {
        this.rawData = data;

        if (!this.amchart) {
            return;
        }
        if (!data) {
            this.amchart.data = null;
            this.ready.next(true);
            return;
        }

        if (this.singleSeries) {
            this.amchart.data = JSON.parse(JSON.stringify(data[0]));

        } else {
            this.amchart.data = JSON.parse(JSON.stringify(data));
        }

        if (!data?.includes(null) && this.isContentEmpty()) {
            this.showNoContentMessage();
        } else {
            if (this.noContentLabel) {
                this.noContentLabel.dom.style.display = 'none';
            }
        }

        this.updateSeriesStyles();
        this.updateCustomLegend();
        this.ready.next(true);
    }

    /**
     * Show 'No Content to Display' message
     */
    public showNoContentMessage(message?: string): void {
        if (!this.noContentLabel) {
            this.noContentLabel = this.amchart.createChild(am4core.Label);
        }
        if (this.options.isSocialStyles) {
            this.noContentLabel.fontSize = '0.875rem';
            this.noContentLabel.fontFamily = 'Poppins, Arial, sans-serif';
            this.noContentLabel.width = am4core.percent(100);
            this.noContentLabel.fill = am4core.color(chartColors.agprGreyTextPlaceholder);
            this.noContentLabel.html = message ? message :
                '<div style="display:flex; align-items:center; min-width: 200px;">'
                +'<span class="fa-duotone fa-regular fa-file-xmark" style="font-size:1.25rem; margin-right:10px; color:'
                + chartColors.agprGreyIcon
                + ';"></span>'
                + this.metadata?.noContentLabel
                + '</div>';
            this.noContentLabel.background.fill = am4core.color(chartColors.agprGreyBackground);
            this.noContentLabel.background.stroke = am4core.color(chartColors.agprGreyBorder);
            this.noContentLabel.paddingBottom = 10;
            this.noContentLabel.paddingTop = 10;
            this.noContentLabel.paddingLeft = 10;
            this.noContentLabel.paddingRight = 10;
            this.noContentLabel.isMeasured = false;
            // @HACK Applying a border radius to the <rect> by setting the rx
            this.noContentLabel.dom.children[0]?.children[0]?.setAttribute('rx', '8');
        } else {
            this.noContentLabel.fontSize = '0.813rem';
            this.noContentLabel.fill = am4core.color(chartColors.agprGrey);
            this.noContentLabel.text = message ? message : this.metadata?.noContentLabel;
            this.noContentLabel.isMeasured = false;
            this.noContentLabel.x = am4core.percent(50);
            this.noContentLabel.y = am4core.percent(50);
            this.noContentLabel.horizontalCenter = 'middle';
        }
        this.noContentLabel.dom.style.display = 'block';
    }

    /**
     * Gets the colors for a single series chart
     * @note For use with `this.updateSeriesStyles`
     */
    protected getSingleSeriesColors(): am4core.Color[] {
        const unusedColors = defaultSeriesColorConfig.slice();
        const seriesColors = [];

        for (let i = 0, l = this.amchart.data.length; i < l; i++) {
            const color = this.amchart.data[i].color;
            if (color) {
                seriesColors.push(am4core.color(color));
                const index = unusedColors.indexOf(color);
                if (index !== -1) {
                    unusedColors.splice(index, 1);
                }
            } else {
                seriesColors.push(undefined);
            }
        }
        for (let i = 0, l = seriesColors.length; i < l; i++) {
            if (!seriesColors[i]) {
                seriesColors[i] = am4core.color(unusedColors.shift());
            }
        }

        return seriesColors;
    }

    /**
     * Updates the chart colors whenever the series data changes
     */
    public abstract updateSeriesStyles(): void;

    /**
     * Modifies the chart to have the right styles and be the right dimensions for export
     */
    public setExportStyles(scale?: number, keepFontsize?: boolean): void {
        if (!keepFontsize) {
            if (this.titleLabel) {
                this.titleLabel.fontSize = 24;
            }

            if (this.primarySeriesLabel && this.singleSeries ) {
                this.primarySeriesLabel.fontSize = 16;
            }

            if (this.secondarySeriesLabel) {
                this.secondarySeriesLabel.fontSize = 16;
            }

            if (this.dateRangeLabel) {
                this.dateRangeLabel.fontSize = 16;
            }

            if (this.noContentLabel) {
                this.noContentLabel.fontSize = 16;
            }
        }

        if (this.primarySeriesLabel && this.singleSeries ) {
            this.primarySeriesLabel.fill = am4core.color(chartColors.agprBlack);
        }

        if (this.noContentLabel) {
            this.noContentLabel.dy = 50;
        }

        if (this.header) {
            this.header.dy = -10;
        }

        if (this.amchart?.chartAndLegendContainer && !this.options.noPadding) {
            this.amchart.chartAndLegendContainer.padding(0, 15, 0, 15);
        }

        if (this.amchart?.htmlContainer && scale) {
            this.amchart.htmlContainer.style.width = (839 * scale) + 'px';
            this.amchart.htmlContainer.style.height = (600 * scale) + 'px';
            this.amchart.htmlContainer.style.position = 'fixed';
        }
    }

    /**
     * Undoes the changes made in setExportStyles to make the chart normal again
     */
    public unsetExportStyles(): void {
        if (this.amchart) {
            this.amchart.htmlContainer.style.position = 'relative';
            this.amchart.htmlContainer.style.height = '100%';
            this.amchart.htmlContainer.style.width = '100%';
            this.amchart.chartAndLegendContainer.padding(0, 0, 0, 0);
        }

        if (this.header) {
            this.header.dy = 0;
        }

        if (this.dateRangeLabel) {
            this.dateRangeLabel.fontSize = '0.781rem';
        }

        if (this.noContentLabel) {
            this.noContentLabel.fontSize = '0.813rem';
            this.noContentLabel.dy = 0;
        }

        if (this.secondarySeriesLabel) {
            this.secondarySeriesLabel.fontSize = '0.781rem';
        }

        if (this.primarySeriesLabel && this.singleSeries) {
            this.primarySeriesLabel.fill = am4core.color(chartColors.agprBlue);
            this.primarySeriesLabel.fontSize = '0.781rem';
        }

        if (this.titleLabel) {
            this.titleLabel.fontSize = '1rem';
        }
    }

    /**
     * Gets the chart as an exported image, which uses the specific chart's implementation of export styles
     *
     * The promise needs to be resolved after the "validated" event fires on the chart, the "validated" event fires after a resize
     * has been finished and all elements of the chart have finished being drawn/animated
     *
     * @TODO: Further investigate disabling animations, doesn't seem easily done
     */
    public async getPNGImageData(scale: number, width: number, height: number, transparent: boolean = false, keepFontsize?: boolean): Promise<string> {

        if (!this.amchart) {
            throw new Error('amchart nullish');
        }

        this.setExportStyles(scale, keepFontsize);

        if (width) {
            this.amchart.htmlContainer.style.width = width + 'px';
        }
        if (height) {
            this.amchart.htmlContainer.style.height = height + 'px';
        }

        // Wait for the chart to redraw to the right size
        await this.chartValidated();

        if (!this.amchart) {
            throw new Error('amchart nullish');
        }

        this.amchart.exporting.useWebFonts = false;

        // individual chart exports look horrible with transparent background, but for
        // the report export, it's desirable
        if (transparent) {
            this.amchart.exporting.backgroundColor = am4core.color('#f00', 0);
        }

        let imageData: string = await this.amchart.exporting.getImage('png');

        // HACK: fixes an issue in safari when exporting charts, SEE AD-11586
        const svgImageData: string = await this.amchart?.exporting.getSVG('svg');

        const url = new URL(window.location.href);
        url.hash = '';
        const cleanUrl = url.toString();
        const escapedUrl = encodeURIComponent(cleanUrl);

        if (svgImageData?.includes(escapedUrl)) {
            imageData = svgImageData.replace(new RegExp(escapedUrl, 'gi'), '');

            imageData = await image.convertSvgDataToDataUrl(imageData, 'png');
        }
        // END HACK

        this.unsetExportStyles();

        // Wait for the chart to redraw back to the right size
        await this.chartValidated();

        if (transparent && this.amchart) {
            this.amchart.exporting.backgroundColor = null;
        }

        return imageData;
    }

    /**
     * Returns after the 'validated' event is sent from the chart one time
     */
    chartValidated(): Promise<void> {
        if (!this.amchart) {
            return;
        }
        return new Promise<void>(
            (resolve) => {
                const handler = this.amchart.events.on(
                    'validated',
                    (ev) => {
                        handler.dispose();

                        // @HACK waiting for some amount of time before confirming validation helps animations on
                        // the chart finish, which is not included as part of the internal validation
                        // not doing this means sometimes charts will just not be rendered correctly on export
                        // requestAnimationFrame doesn't seem to be as effective
                        // Especially for bar chart, it recalcutes bar size and label position
                        setTimeout(() => {
                            resolve();
                        }, 150);
                    }
                );
            }
        );
    }

    /**
     * Gets the label for the series, for single series this should be a single string, for multi-series it doesn't exist
     */
    public getExportSeriesLabel(): string {
        if (this.singleSeries &&
            this.series &&
            this.series[0]
        ) {
            return this.series[0].label || '';
        } else {
            return '';
        }
    }
}
